import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import ShareHeader from '../components/ShareHeader'
import ShareMediaArea from '../components/ShareMediaArea'
import ShareFormArea from '../components/ShareFormArea'
import { shareService } from '../services/shareService'

const Share = () => {
  const [searchParams] = useSearchParams()
  const shareCode = searchParams.get('shareCode')
  
  const [shareInfo, setShareInfo] = useState(null)
  const [objectId, setObjectId] = useState('')
  const [sharePixId, setSharePixId] = useState('')
  const [satsueiMemoCategoryId, setSatsueiMemoCategoryId] = useState('')
  const [selectedMediaType, setSelectedMediaType] = useState('image')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadShareInfo = useCallback(async () => {
    if (!shareCode) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await shareService.getShareInfo(shareCode)
      setShareInfo(data)
      setObjectId(data.objectId)
      setSharePixId(data.pixId)
      
      // Check if object exists
      const objectCount = await shareService.getObjectCount(data.objectId, true, data.pixId)
      if (objectCount.objectCount === 0) {
        setError('共有者により削除されています。')
        return
      }
      
      // Load initial object data to get category
      const objectData = await shareService.getObject(data.objectId, null, true, data.pixId)
      if (objectData.returnCode === '200') {
        setSatsueiMemoCategoryId(objectData.satsueiMemoCategoryId)
      }
    } catch (error) {
      console.error('Failed to load share info:', error)
      setError('共有情報の読み込みに失敗しました。')
    } finally {
      setIsLoading(false)
    }
  }, [shareCode])

  useEffect(() => {
    if (shareCode) {
      loadShareInfo()
    }
  }, [shareCode, loadShareInfo])

  const handleMediaTypeChange = (mediaType) => {
    setSelectedMediaType(mediaType)
  }

  const handleSatsueiMemoCategoryChange = (categoryId) => {
    setSatsueiMemoCategoryId(categoryId)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100" data-testid="share-page">
        <ShareHeader shareCode={shareCode || ''} />
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64" data-testid="loading-container">
            <div className="text-gray-500 text-lg" data-testid="loading-message">読み込み中...</div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100" data-testid="share-page">
        <ShareHeader shareCode={shareCode || ''} />
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-testid="error-container">
            <div className="text-center">
              <div className="text-red-600 text-lg mb-4" data-testid="error-message">{error}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!shareInfo || !objectId || !sharePixId) {
    return (
      <div className="min-h-screen bg-gray-100" data-testid="share-page">
        <ShareHeader shareCode={shareCode || ''} />
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-testid="not-found-container">
            <div className="text-center text-gray-500 text-lg" data-testid="not-found-message">
              共有情報が見つかりません。
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="share-page">
      <ShareHeader shareCode={shareCode} />
      
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="main-content">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel - Media Area */}
          <div className="lg:col-span-1" data-testid="media-panel">
            <ShareMediaArea
              objectId={objectId}
              sharePixId={sharePixId}
              selectedMediaType={selectedMediaType}
              onMediaTypeChange={handleMediaTypeChange}
            />
          </div>

          {/* Right Panel - Form Area */}
          <div className="lg:col-span-2" data-testid="form-panel">
            <ShareFormArea
              objectId={objectId}
              sharePixId={sharePixId}
              satsueiMemoCategoryId={satsueiMemoCategoryId}
              onSatsueiMemoCategoryChange={handleSatsueiMemoCategoryChange}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Share
